import { useEffect, useState } from "react";
import { CheckCircle2, Circle, Loader2 } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { useTenant } from "@/contexts/TenantContext";

interface Step {
  label: string;
  duration: number;
}

interface ProgressTimelineProps {
  steps: Step[];
  currentStep: number;
}

export const ProgressTimeline = ({ steps, currentStep }: ProgressTimelineProps) => {
  const { t } = useI18n();
  const { currentTenant } = useTenant();
  const [timeRemaining, setTimeRemaining] = useState(0);

  useEffect(() => {
    // Calculate total time remaining
    const totalTime = steps.reduce((acc, step) => acc + step.duration, 0);
    const elapsedTime = steps.slice(0, currentStep).reduce((acc, step) => acc + step.duration, 0);
    setTimeRemaining(Math.max(0, totalTime - elapsedTime));

    // Countdown timer
    const interval = setInterval(() => {
      setTimeRemaining((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [currentStep, steps]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="w-full max-w-2xl mx-auto px-4 space-y-8 animate-fade-in">
      {/* Header */}
      <div className="text-center space-y-3">
        <h2 className="text-2xl md:text-3xl font-light text-foreground">
          {t('timeline.title')}
        </h2>
        <div 
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border"
          style={{
            backgroundColor: currentTenant?.primaryColor ? `${currentTenant.primaryColor}1A` : undefined,
            color: currentTenant?.primaryColor || undefined,
            borderColor: currentTenant?.primaryColor ? `${currentTenant.primaryColor}33` : undefined,
          }}
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          <span className="font-medium">{t('timeline.eta')}</span>
          <span className="font-mono">{formatTime(timeRemaining)}</span>
        </div>
        <div className="sr-only" aria-live="polite" aria-atomic="true">
          {`Step ${currentStep} of ${steps.length}: ${steps[currentStep - 1]?.label || 'Starting'}`}
        </div>
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Progress Line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border"></div>
        <div
          className="absolute left-6 top-0 w-0.5 transition-all duration-1000 ease-out"
          style={{
            height: `${(currentStep / steps.length) * 100}%`,
            background: currentTenant?.primaryColor 
              ? `linear-gradient(135deg, ${currentTenant.primaryColor}, ${currentTenant.primaryColor}dd)`
              : undefined,
          }}
        ></div>

        {/* Steps */}
        <div className="space-y-8">
          {steps.map((step, index) => {
            const isCompleted = currentStep > index;
            const isCurrent = currentStep === index;
            const isUpcoming = currentStep < index;

            return (
              <div
                key={index}
                className={`relative flex items-start gap-4 transition-all duration-500 ${
                  isUpcoming ? "opacity-40" : "opacity-100"
                }`}
              >
                {/* Icon */}
                <div className="relative z-10 flex-shrink-0">
                  {isCompleted ? (
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center shadow-medium"
                      style={{
                        background: currentTenant?.primaryColor 
                          ? `linear-gradient(135deg, ${currentTenant.primaryColor}, ${currentTenant.primaryColor}dd)`
                          : undefined,
                      }}
                    >
                      <CheckCircle2 className="w-6 h-6 text-white" strokeWidth={2.5} />
                    </div>
                  ) : isCurrent ? (
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center shadow-medium animate-pulse"
                      style={{
                        background: currentTenant?.primaryColor 
                          ? `linear-gradient(135deg, ${currentTenant.primaryColor}, ${currentTenant.primaryColor}dd)`
                          : undefined,
                      }}
                    >
                      <Loader2 className="w-6 h-6 text-white animate-spin" strokeWidth={2.5} />
                    </div>
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                      <Circle className="w-6 h-6 text-muted-foreground" strokeWidth={2} />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 pt-2">
                  <div className="bg-card rounded-2xl p-4 shadow-soft border border-border">
                    <h3
                      className={`text-base md:text-lg font-medium ${
                        isCompleted || isCurrent ? "text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {step.label}
                    </h3>
                    {isCurrent && (
                      <p 
                        className="text-sm mt-1 font-medium"
                        style={{ color: currentTenant?.primaryColor || undefined }}
                      >
                        ~{step.duration}s
                      </p>
                    )}
                    {isCompleted && (
                      <p 
                        className="text-sm mt-1"
                        style={{ color: currentTenant?.primaryColor || undefined }}
                      >
                        ✓ {t('timeline.complete')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer Message */}
      <div className="text-center pt-4">
        <p className="text-sm text-muted-foreground leading-relaxed">
          {t('timeline.wait_message')}
          <br />
          {t('timeline.duration_note')}
        </p>
      </div>
    </div>
  );
};
